import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Calendar, Clock, ArrowRight, ChevronLeft } from "lucide-react";
import { format, parseISO } from "date-fns";
import { trackEvent } from "../hooks/useAnalytics";
import { convertDropboxUrl } from "../utils/dropboxUtils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Estimated reading time based on word count
const getReadingTime = (content) => {
  const text = content.replace(/<[^>]*>/g, '');
  const words = text.split(/\s+/).length;
  const minutes = Math.ceil(words / 200);
  return minutes;
};

const ArticleCard = ({ article }) => {
  const readingTime = getReadingTime(article.content);
  
  return (
    <Link 
      to={`/artiklar/${article.slug}`}
      className="group block bg-[#171717] border border-white/5 hover:border-[#EA580C]/30 transition-all duration-300"
      data-testid={`article-card-${article.slug}`}
    >
      {article.featured_image && (
        <div className="aspect-video overflow-hidden">
          <img 
            src={convertDropboxUrl(article.featured_image)} 
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
      )}
      <div className="p-6">
        <div className="flex items-center gap-4 text-white/50 text-sm mb-3">
          <span className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            {format(parseISO(article.created_at), "MMM d, yyyy")}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            {readingTime} min read
          </span>
        </div>
        
        <h2 className="font-anton text-xl text-white group-hover:text-[#EA580C] transition-colors mb-3">
          {article.title}
        </h2>
        
        <p className="text-white/60 text-sm line-clamp-2 mb-4">
          {article.excerpt}
        </p>
        
        {article.tags && article.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {article.tags.slice(0, 3).map((tag, i) => (
              <span 
                key={i}
                className="px-2 py-1 bg-[#2e3104] text-[#EA580C] text-xs uppercase tracking-wider"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
        
        <span className="inline-flex items-center gap-2 text-[#EA580C] text-sm font-semibold group-hover:gap-3 transition-all">
          Read More <ArrowRight className="w-4 h-4" />
        </span>
      </div>
    </Link>
  );
};

const ArticlesList = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    trackEvent('page_view', '/artiklar');
    
    const fetchArticles = async () => {
      try {
        const response = await axios.get(`${API}/articles`);
        setArticles(response.data.articles);
      } catch (e) {
        console.error("Failed to fetch articles:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="bg-[#171717] border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-4">
            <ChevronLeft className="w-5 h-5 text-white/60" />
            <span className="font-anton text-xl text-white">ARCTIC RIDERS</span>
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-[#171717] to-[#0A0A0A] py-16 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="font-anton text-4xl md:text-6xl text-white mb-4">
            Adventure Journal
          </h1>
          <p className="font-manrope text-white/60 max-w-2xl mx-auto text-lg">
            Utforska Stockholms bästa motorcykelrutter, restips och berättelser från vägen.
            Din guide till äventyrsmotorcykel i Stockholm och omnejd.
          </p>
        </div>
      </section>

      {/* Articles Grid */}
      <main className="max-w-6xl mx-auto px-6 py-12">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-4 border-[#EA580C] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : articles.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-white/60 text-lg">No articles yet. Check back soon!</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {articles.map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>
        )}
      </main>

      {/* CTA Section */}
      <section className="bg-[#171717] border-t border-white/10 py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-anton text-3xl text-white mb-4">
            Redo för ditt äventyr?
          </h2>
          <p className="text-white/60 mb-8 max-w-xl mx-auto">
            Boka din motorcykeluthyrning eller guidade tur idag och upplev Stockholm på ett helt nytt sätt.
          </p>
          <Link
            to="/"
            className="inline-block bg-[#EA580C] hover:bg-[#C2410C] text-white px-8 py-4 font-semibold uppercase tracking-wider transition-colors"
            data-testid="articles-cta-button"
          >
            Boka nu
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A0A0A] border-t border-white/10 py-8 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-white/40 text-sm">
            © {new Date().getFullYear()} Arctic Riders · Org.nr 559205-3432 · Motorcykeläventyr i Stockholm.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ArticlesList;
